
/**
 * CanvasUIComponent - actor component for rendering interactive VR UI panels using CanvasUI.
 * Supports head tracking, VR controller interaction, and various UI element types.
 */

import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';
import { CanvasUI } from '../ui/CanvasUI.js';

import { VRBaseComponent } from './VRBaseComponent.js';

import type { VRBaseComponentOptions } from './VRBaseComponent.js';
import type { CanvasUIConfig, CanvasUIContent } from '../ui/CanvasUI.js';

export interface CanvasUIComponentOptions extends VRBaseComponentOptions {
  /** Panel size in world units */
  panelSize?: { width: number; height: number };
  /** Canvas resolution */
  canvasSize?: { width: number; height: number };
  /** Panel opacity */
  opacity?: number;
  /** UI content configuration */
  content?: CanvasUIContent;
  /** UI element configuration */
  uiConfig?: CanvasUIConfig;
  /** Auto-attach to VR controllers */
  enableVRInteraction?: boolean;
  /** Auto-position relative to VR headset */
  enableHeadTracking?: boolean;
  /** Position offset from head when head tracking is enabled */
  headTrackingOffset?: THREE.Vector3;
  /** Rotation offset from head when head tracking is enabled */
  headTrackingRotation?: THREE.Euler;
  /** Whether this panel blocks interactions with panels behind it (default true) */
  blockInteractionsBehind?: boolean;
  /** Enable debug cube visualization for head tracking */
  enableDebugCubes?: boolean;
}

@EngineClass('CanvasUIComponent')
export class CanvasUIComponent extends VRBaseComponent {


  @property()
  public panelSize: { width: number; height: number } = { width: 1, height: 1 };

  @property()
  public canvasSize: { width: number; height: number } = { width: 512, height: 512 };

  @property({ type: 'number', min: 0, max: 1, step: 0.1 })
  public opacity: number = 1.0;

  @property()
  public content: CanvasUIContent = {};

  @property()
  public uiConfig: CanvasUIConfig = {
      body: {
        fontFamily: 'Arial',
        fontSize: 14,
        backgroundColor: '#0f0f23',
        fontColor: '#f8fafc',
        borderRadius: 10,
        textAlign: 'center'
      }
    };

  @property()
  public enableVRInteraction: boolean = true;

  @property()
  public enableHeadTracking: boolean = false;

  @property()
  public headTrackingOffset: THREE.Vector3 = new THREE.Vector3(0, -0.2, -0.5);

  @property()
  public headTrackingRotation: THREE.Euler = new THREE.Euler(0, 0, 0);

  @property()
  public blockInteractionsBehind: boolean = true;

  @property()
  public enableDebugCubes: boolean = false;


  public readonly onElementSelect: Delegate<[string], void> = new Delegate();
  public readonly onElementChange: Delegate<[string, unknown], void> = new Delegate();

  private canvasUI: CanvasUI | null = null;
  private isInitialized = false;
  private attachedCamera: THREE.Object3D | null = null;
  private attachmentFailed = false;
  private debugCube: THREE.Mesh | null = null;

  public override initialize(options?: CanvasUIComponentOptions): void {
    super.initialize(options);
    this.panelSize = options?.panelSize ?? this.panelSize;
    this.canvasSize = options?.canvasSize ?? this.canvasSize;
    this.opacity = options?.opacity ?? this.opacity;
    this.content = options?.content ?? this.content;
    this.uiConfig = options?.uiConfig ?? this.uiConfig;
    this.enableVRInteraction = options?.enableVRInteraction ?? this.enableVRInteraction;
    this.enableHeadTracking = options?.enableHeadTracking ?? this.enableHeadTracking;
    this.headTrackingOffset = options?.headTrackingOffset ?? this.headTrackingOffset;
    this.headTrackingRotation = options?.headTrackingRotation ?? this.headTrackingRotation;
    this.blockInteractionsBehind = options?.blockInteractionsBehind ?? this.blockInteractionsBehind;
    this.enableDebugCubes = options?.enableDebugCubes ?? this.enableDebugCubes;
  }



  public override beginPlay(): void {
    super.beginPlay();
    if (DEBUG) console.log('🎨 CanvasUIComponent: Beginning play, initializing CanvasUI...');
    this.initializeCanvasUI();
  }

  public override endPlay(): void {
    if (this.canvasUI) {
      // Clean up CanvasUI resources and remove event listeners
      try {
        // Remove controller event listeners to prevent handedness errors
        const canvasUI = this.canvasUI as any;
        if (canvasUI.controller) {
          canvasUI.controller.removeEventListener('select', canvasUI.onSelect);
          canvasUI.controller.removeEventListener('selectstart', canvasUI.onSelectStart);
          canvasUI.controller.removeEventListener('selectend', canvasUI.onSelectEnd);
        }
        if (canvasUI.controller1) {
          canvasUI.controller1.removeEventListener('select', canvasUI.onSelect);
          canvasUI.controller1.removeEventListener('selectstart', canvasUI.onSelectStart);
          canvasUI.controller1.removeEventListener('selectend', canvasUI.onSelectEnd);
        }
      } catch (error) {
        // Ignore cleanup errors during VR session end
      }

      // Detach from camera if attached
      this.detachFromCamera();

      // Remove mesh from parent before disposing
      if (this.canvasUI.mesh.parent) {
        this.canvasUI.mesh.parent.remove(this.canvasUI.mesh);
      }

      // Use the proper dispose method
      this.canvasUI.dispose();
    }

    // Clean up debug cube
    if (this.debugCube) {
      if (this.debugCube.parent) {
        this.debugCube.parent.remove(this.debugCube);
      }
      this.debugCube = null;
    }
    super.endPlay();
  }

  public override tickPostPhysics(deltaTime: number): void {
    if (this.canvasUI) {
      this.canvasUI.update();

      // Attach to camera if head tracking is enabled
      if (this.enableHeadTracking) {
        this.updateHeadTrackedPosition();
      }
    }
    super.tickPostPhysics(deltaTime);
  }

  public getObject3D(): THREE.Object3D | null {
    return this.canvasUI?.mesh ?? null;
  }

  private initializeCanvasUI(): void {
    if (this.isInitialized) return;

    const DEBUG = true;
    const world = this.getWorld();
    const gameLoop = world?.gameLoop;
    const renderer = gameLoop?.renderer?.native.asWebGL();
    const scene = world?.scene ?? null;

    if (DEBUG) console.log('🔧 CanvasUIComponent: GameLoop found:', !!gameLoop);
    if (DEBUG) console.log('🔧 CanvasUIComponent: Renderer found:', !!renderer);
    if (DEBUG) console.log('🔧 CanvasUIComponent: Scene found:', !!scene);

    if (!renderer) {
      console.error('❌ CanvasUIComponent: No renderer available');
      return;
    }

    if (!scene) {
      console.error('❌ CanvasUIComponent: No scene available');
      return;
    }

    const config: CanvasUIConfig = {
      panelSize: this.panelSize,
      width: this.canvasSize.width,
      height: this.canvasSize.height,
      opacity: this.opacity,
      body: {
        fontFamily: 'Arial',
        fontSize: 14,
        backgroundColor: '#1a1a2e',
        fontColor: '#ffffff',
        borderRadius: 10,
        textAlign: 'center',
        ...this.uiConfig.body
      },
      renderer,
      scene,
      ...this.uiConfig
    };

    if (DEBUG) console.log('🎨 CanvasUIComponent: Creating CanvasUI with config:', {
      hasRenderer: !!config.renderer,
      hasScene: !!config.scene,
      panelSize: config.panelSize,
      width: config.width,
      height: config.height
    });

    try {
      // Setup onSelect callbacks for button elements to bridge to component delegates
      this.setupElementCallbacks(config);

      this.canvasUI = new CanvasUI(this.content, config);

      // Set interaction blocking option
      this.canvasUI.setBlockInteractionsBehind(this.blockInteractionsBehind);

      // Add the mesh to hierarchy or scene based on head tracking
      const object3D = this.canvasUI.mesh;
      if (object3D) {
        // Set depth test based on option
        const material = object3D.material as THREE.MeshBasicMaterial;
        if (material) {
          material.depthTest = this.blockInteractionsBehind;
          material.depthWrite = this.blockInteractionsBehind;
          // Set render order to ensure proper depth sorting
          object3D.renderOrder = this.blockInteractionsBehind ? 0 : 1000;
          if (DEBUG) console.log('🔍 CanvasUIComponent: Depth test/write set to:', this.blockInteractionsBehind, 'renderOrder:', object3D.renderOrder);
        }

        if (this.enableHeadTracking) {
          // Set initial position and rotation offsets for camera attachment
          object3D.position.copy(this.headTrackingOffset);
          object3D.rotation.copy(this.headTrackingRotation);
          // Add to scene temporarily - will be moved to camera in tickPostPhysics
          const world = this.getWorld();
          if (world?.scene) {
            world.scene.add(object3D);
            if (DEBUG) console.log('✅ CanvasUIComponent: CanvasUI mesh added to scene temporarily for head tracking');
          }

          // Create multiple debug objects at different positions
          if (world?.scene && this.enableDebugCubes) {
            // Large red cube at UI position - should be very visible
            const cubeGeometry = new THREE.BoxGeometry(0.5, 0.5, 0.5);
            const cubeMaterial = new THREE.MeshBasicMaterial({
              color: 0xff0000,
              wireframe: false,
              transparent: false
            });
            this.debugCube = new THREE.Mesh(cubeGeometry, cubeMaterial);
            // Start at a fixed world position so we can see it initially
            this.debugCube.position.set(0, 2, -2);
            this.debugCube.rotation.copy(this.headTrackingRotation);
            world.scene.add(this.debugCube);

            // Green sphere at origin
            const sphereGeometry = new THREE.SphereGeometry(0.1);
            const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
            const debugSphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
            debugSphere.position.set(0, 0, 0);
            world.scene.add(debugSphere);

            // Large blue cube very close to camera origin
            const blueCubeGeometry = new THREE.BoxGeometry(1, 1, 1);
            const blueCubeMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
            const blueCube = new THREE.Mesh(blueCubeGeometry, blueCubeMaterial);
            blueCube.position.set(0, 0, -0.1);
            world.scene.add(blueCube);

            // Huge yellow cube further away
            const yellowCubeGeometry = new THREE.BoxGeometry(2, 2, 2);
            const yellowCubeMaterial = new THREE.MeshBasicMaterial({ color: 0xffff00 });
            const yellowCube = new THREE.Mesh(yellowCubeGeometry, yellowCubeMaterial);
            yellowCube.position.set(0, 0, -3);
            world.scene.add(yellowCube);

            if (DEBUG) console.log('🔴 CanvasUIComponent: Debug objects added to scene');
          }
        } else {
          // Add to component hierarchy for normal positioning
          this.add(object3D);
          if (DEBUG) console.log('✅ CanvasUIComponent: CanvasUI mesh added to component hierarchy');
        }
        if (DEBUG) console.log('👁️ CanvasUIComponent: Mesh visible:', object3D.visible, 'position:', object3D.position);
      }

      this.isInitialized = true;
      if (DEBUG) console.log('🎉 CanvasUIComponent: CanvasUI initialized successfully');

    } catch (error) {
      console.error('❌ CanvasUIComponent: Failed to initialize CanvasUI:', error);
    }
  }

  private setupElementCallbacks(config: CanvasUIConfig): void {
    // Setup onSelect callbacks for button elements to bridge to component delegates
    Object.entries(config).forEach(([elementName, elementConfig]) => {
      if (typeof elementConfig === 'object' && elementConfig !== null && 'type' in elementConfig) {
        if (elementConfig.type === 'button' && !elementConfig.onSelect) {
          elementConfig.onSelect = () => {
            if (DEBUG) console.log(`🎯 CanvasUIComponent: Button "${elementName}" clicked`);
            this.onElementSelect.invoke(elementName);
          };
        } else if (elementConfig.type === 'picker' && !elementConfig.onChange) {
          elementConfig.onChange = (value: unknown) => {
            if (DEBUG) console.log(`🎨 CanvasUIComponent: Picker "${elementName}" changed to:`, value);
            this.onElementChange.invoke(elementName, value);
          };
        } else if (elementConfig.type === 'slider' && !elementConfig.onChange) {
          elementConfig.onChange = (value: unknown) => {
            if (DEBUG) console.log(`🎚️ CanvasUIComponent: Slider "${elementName}" changed to:`, value);
            this.onElementChange.invoke(elementName, value);
          };
        } else if (elementConfig.type === 'input-text') {
          elementConfig.onChanged ??= (text: string) => {
            if (DEBUG) console.log(`📝 CanvasUIComponent: Input "${elementName}" changed to:`, text);
            this.onElementChange.invoke(elementName, text);
          };
          elementConfig.onEnter ??= (text: string) => {
            if (DEBUG) console.log(`⏎ CanvasUIComponent: Input "${elementName}" entered:`, text);
            this.onElementChange.invoke(elementName, text);
          };
        }
      }
    });
  }

  public updateElement(elementName: string, content: string): void {
    if (this.canvasUI) {
      this.canvasUI.updateElement(elementName, content);
    }
  }

  public updateConfig(elementName: string, property: string, value: unknown): void {
    if (this.canvasUI) {
      this.canvasUI.updateConfig(elementName, property, value);
    }
  }

  public getCanvasUI(): CanvasUI | null {
    return this.canvasUI;
  }

  private attachToCamera(): void {
    if (!this.canvasUI?.mesh) return;

    const world = this.getWorld();
    const camera = world?.getFirstPlayerPawn()?.getCamera();

    if (!camera) return;

    // Check if already attached to this camera
    if (this.attachedCamera === camera && this.canvasUI.mesh.parent === camera) {
      return; // Already attached
    }

    try {
      // Remove from current parent if any
      if (this.canvasUI.mesh.parent) {
        this.canvasUI.mesh.parent.remove(this.canvasUI.mesh);
      }

      // Apply the head tracking offset and rotation
      this.canvasUI.mesh.position.copy(this.headTrackingOffset);
      this.canvasUI.mesh.rotation.copy(this.headTrackingRotation);

      // Add the mesh to the camera
      camera.add(this.canvasUI.mesh);

      // Also attach debug cube to camera with same offsets
      if (this.debugCube && this.enableDebugCubes) {
        if (this.debugCube.parent) {
          this.debugCube.parent.remove(this.debugCube);
        }
        // Make debug cube larger and more visible when attached to camera
        this.debugCube.scale.set(3, 3, 3);
        // Try multiple positions - start with right in front
        this.debugCube.position.set(0, 0, -0.5);
        this.debugCube.rotation.set(0, 0, 0);
        camera.add(this.debugCube);

        // Add more debug cubes at different positions
        for (let i = 0; i < 4; i++) {
          const extraCube = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.2, 0.2),
            new THREE.MeshBasicMaterial({ color: 0xff00ff })
          );
          const angle = (i / 4) * Math.PI * 2;
          extraCube.position.set(Math.cos(angle) * 0.8, Math.sin(angle) * 0.8, -1);
          camera.add(extraCube);
        }
      }

      this.attachedCamera = camera;
      this.attachmentFailed = false;

    } catch (error) {
      this.attachmentFailed = true;
    }
  }

  private updateHeadTrackedPosition(): void {
    if (!this.canvasUI?.mesh) return;

    const world = this.getWorld();
    const camera = world?.getFirstPlayerPawn()?.getCamera();
    if (!camera) return;

    // Attach to camera if not already attached
    this.attachToCamera();
  }

  private detachFromCamera(): void {
    if (this.attachedCamera && this.canvasUI?.mesh) {
      try {
        if (this.canvasUI.mesh.parent === this.attachedCamera) {
          this.attachedCamera.remove(this.canvasUI.mesh);
        }
      } catch (error) {
        // Ignore cleanup errors
      }
      this.attachedCamera = null;
      this.attachmentFailed = false;
    }
  }
}

const DEBUG = true;